import React, { useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { ButtonSize, RegularButton } from "@/stories/button/button";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { initBranch, trackEvent } from "@/app/utils/branchio";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
import { ThirdPartyData } from "@/app/interface/docvault";
import useMobile from "@/app/hooks/use-mobile";
import CircleTick from "@/app/assets/icons/circle-tick";
import { CSK_ISIN } from "@/app/utils/constants";
import { useTransactionFlow } from "@/context/TransactionContext";
import { sendMetaConversionEvent } from "@/app/utils/metaConversion";
import { getGTM } from "@/app/utils/gtm";

interface PaymentStepProps {
    onComplete: () => void;
    thirdPartyData: ThirdPartyData | undefined;
    userId?: string | undefined;
    userNotInvested?: boolean;
    isin?: string;
}

const SuccessModal: React.FC<PaymentStepProps> = ({
    thirdPartyData,
    userId,
    userNotInvested,
    isin,
}) => {
    const { isMobile } = useMobile();
    const { state } = useTransactionFlow();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    useEffect(() => {
        setTimeout(() => {
            if (!state?.postDigioTxnData?.product || !state?.postDigioTxnData?.amount) return;
            const baseEventId = crypto.randomUUID();
            const productEventId = crypto.randomUUID();
            const eventNameBase = "US_Payment_Done";
            const product = state?.postDigioTxnData?.product;
            const amount = state?.postDigioTxnData?.amount;
            const email = thirdPartyData?.email;

            // Set analytics
            setAnalyticsEvent("", eventNameBase);

            // Facebook Pixel + Meta Conversion
            (async () => {
                const ReactPixel = (await import("react-facebook-pixel")).default;
                ReactPixel.trackCustom(eventNameBase, {
                    value: amount,
                    currency: "INR",
                    eventID: baseEventId,
                });

                await sendMetaConversionEvent({
                    eventName: eventNameBase,
                    eventData: { userId, value: amount, currency: "INR" },
                    eventId: baseEventId,
                });
                ReactPixel.trackCustom(`${eventNameBase}_${product}`, {
                    value: amount,
                    currency: "INR",
                    eventID: productEventId,
                });

                await sendMetaConversionEvent({
                    eventName: `${eventNameBase}_${product}`,
                    eventData: { userId, value: amount, currency: "INR" },
                    eventId: productEventId,
                });
            })();
            // GTM Event
            getGTM().then((gtm) => {
                gtm.dataLayer({
                    dataLayer: {
                        event: `${eventNameBase}_GTM`,
                        email,
                        value: state?.postDigioTxnData?.amount,
                        currency: "INR",
                    },
                });
            });

            getGTM().then((gtm) => {
                gtm.dataLayer({
                    dataLayer: {
                        event: `${eventNameBase}_${product}_GTM`,
                        email,
                        value: state?.postDigioTxnData?.amount,
                        currency: "INR",
                    },
                });
            });

            // Branch Events - Generic US_Payment_Done event (device-agnostic)
            initBranch()
                .then(() => {
                    trackEvent(
                        eventNameBase,
                        thirdPartyData?.email || "",
                        thirdPartyData?.phone || "",
                        userId || "",
                        state?.postDigioTxnData?.amount?.toString() || "",
                        product,
                        "INR",
                    );
                    cleverTapTrackEvent(eventNameBase, {
                        email: thirdPartyData?.email || "",
                        phone: thirdPartyData?.phone || "",
                        userId: userId || "",
                        amount: state?.postDigioTxnData?.amount?.toString() || "",
                        product: product,
                        currency: "INR",
                    });
                    // trackEvent(
                    //     `${eventNameBase}_${product}`,
                    //     thirdPartyData?.email || "",
                    //     thirdPartyData?.phone || "",
                    //     userId || "",
                    //     state?.postDigioTxnData?.amount?.toString() || "",
                    //     "INR",
                    // );
                    cleverTapTrackEvent(`${eventNameBase}_${product}`, {
                        email: thirdPartyData?.email || "",
                        phone: thirdPartyData?.phone || "",
                        userId: userId || "",
                        amount: state?.postDigioTxnData?.amount?.toString() || "",
                        currency: "INR",
                    });
                })
                .catch((error) => {
                    console.error("Error initializing Branch:", error);
                });
        }, 350);
    }, []);

    const handleRedirect = () => {
        setAnalyticsEvent("", "KYC_High_Intent_Explore_Unlisted_Shares");
        cleverTapTrackEvent("KYC_High_Intent_Explore_Unlisted_Shares", {
            email: thirdPartyData?.email || "",
            amount: state?.postDigioTxnData?.amount?.toString() || "",
            currency: "INR",
        });
        // initBranch()
        //     .then(() => {
        //         trackEvent(
        //             "KYC_High_Intent_Explore_Unlisted_Shares",
        //             thirdPartyData?.email || "",
        //             state?.postDigioTxnData?.amount?.toString() || "",
        //             "INR",
        //         );
        //     })

        //     .catch((error) => console.error("Error initializing Branch:", error));
        window.location.href = `${process.env.ICM_UE_POST_URL_LISTING}`;
    };

    return (
        <Dialog open={true}>
            <DialogContent className="w-[90%] !rounded-3xl p-6 md:w-[460px] md:p-9 [&>button:last-child]:hidden">
                <VisuallyHidden.Root>
                    <DialogTitle>Success Modal</DialogTitle>
                </VisuallyHidden.Root>
                <div className="w-full">
                    <div className="flex-center flex flex-col items-center">
                        {/* <div className="h-36 w-36 md:h-44 md:w-44">
                            <Lottie animationData={Tick} loop={false} />
                        </div> */}
                        <CircleTick size={32} className="my-s6 text-error-600 md:my-s8" />
                        <p className="w-full text-center text-md font-semibold md:text-lg">
                            Thank you <br /> Your order has been placed successfully!
                        </p>
                        {userNotInvested && isin === CSK_ISIN ? (
                            <p className="mb-0 mt-s6 w-full text-center text-sm md:text-md">
                                Your ₹200 Amazon voucher will be sent to your email ID within 5 days
                            </p>
                        ) : (
                            <p className="mb-0 mt-s6 w-full text-center text-sm md:text-md">
                                Units will be credited to your Demat account subject to the
                                realization of payment
                            </p>
                        )}
                    </div>
                </div>
                <RegularButton
                    className="mt-4 max-w-full text-lg md:mt-s11"
                    label="Explore Unlisted Shares"
                    size={isMobile ? ButtonSize.Small : ButtonSize.Large}
                    noIcon={true}
                    showRightIcon={false}
                    onClick={handleRedirect}
                />
            </DialogContent>
        </Dialog>
    );
};

export default SuccessModal;
